import React from "react";
import { WOOD_VARIANTS } from "../mock";
import Reveal from "./Reveal";
import { Ruler, TreePine, Droplets, X, ChevronLeft, ChevronRight } from "lucide-react";

const FEATURES = [
  { t: "Dimensiones", v: "20×15×3 cm", s: "Tamaño ideal para cualquier espacio", icon: Ruler },
  { t: "Material", v: "Taco de madera blanca", s: "Madera blanca de alta calidad con acabado premium", icon: TreePine },
  { t: "Impresión", v: "Tintas UV", s: "Alta calidad y durabilidad excepcional", icon: Droplets },
];

function Lightbox({ items, index, onClose, onPrev, onNext }) {
  React.useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, onClose, onPrev, onNext]);

  if (index == null) return null;
  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in"
      onClick={onClose}
      data-testid="mockup-lightbox"
    >
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="mockup-lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>

      <button
        type="button"
        aria-label="Anterior"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="mockup-lightbox-prev"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        type="button"
        aria-label="Siguiente"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="mockup-lightbox-next"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <figure
        className="relative max-w-5xl w-full max-h-[88vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={item.image}
          alt={item.label}
          className="w-auto max-w-full max-h-[78vh] object-contain rounded-xl shadow-2xl"
        />
        <figcaption className="mt-4 text-center text-sm tracking-[0.18em] uppercase text-white/70">
          {item.label}
        </figcaption>
      </figure>
    </div>
  );
}

export default function Quality() {
  const [lightboxIdx, setLightboxIdx] = React.useState(null);

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const prevImg = () =>
    setLightboxIdx((v) => (v == null ? null : (v - 1 + WOOD_VARIANTS.length) % WOOD_VARIANTS.length));
  const nextImg = () =>
    setLightboxIdx((v) => (v == null ? null : (v + 1) % WOOD_VARIANTS.length));

  return (
    <section id="calidad" className="py-20 md:py-32 bg-[#efe6cf]/40 border-t border-[#e8ddc3]/60 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-b from-[#e8ddc3]/40 to-transparent blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        <Reveal className="text-center mb-12 md:mb-20">
          <div className="text-[11px] md:text-xs tracking-[0.25em] uppercase text-[#b59557] mb-4">
            Calidad Premium
          </div>
          <h2 className="font-serif font-medium text-[#1a1816] text-3xl md:text-5xl leading-tight">
            Madera{" "}
            <span className="italic text-[#b59557] font-light">blanca</span>
          </h2>
          <p className="mt-4 text-[#4a4136] text-sm md:text-base max-w-2xl mx-auto">
            Cada retrato se imprime directamente sobre un taco de madera blanca con tintas UV de
            alta calidad, garantizando colores vibrantes y durabilidad excepcional.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.t} delay={i + 1}>
                <div className="bg-white rounded-2xl p-8 border border-[#ece3cc] text-center h-full transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#b59557]/10">
                  <div className="mx-auto h-12 w-12 rounded-full bg-[#efe6cf] border border-[#e2d5b3] flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-[#8a7340]" strokeWidth={1.6} />
                  </div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[#b59557] mb-2">
                    {f.t}
                  </div>
                  <div className="font-serif text-xl md:text-2xl text-[#1a1816]">{f.v}</div>
                  <div className="mt-3 text-sm text-[#4a4136] leading-relaxed">{f.s}</div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {WOOD_VARIANTS.map((w, i) => (
            <Reveal key={w.label} delay={(i % 3) + 1}>
              <figure className="group">
                <button
                  type="button"
                  onClick={() => openLightbox(i)}
                  className="relative block w-full aspect-square rounded-2xl overflow-hidden bg-white border border-[#ece3cc] transition-transform duration-500 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#b59557] focus:ring-offset-2 focus:ring-offset-[#efe6cf] cursor-zoom-in"
                  aria-label={`Ampliar ${w.label}`}
                  data-testid={`mockup-thumb-${i}`}
                >
                  <img
                    src={w.image}
                    alt={w.label}
                    className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                </button>
                <figcaption className="mt-3 text-center text-sm text-[#4a4136]">{w.label}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="text-center mt-12 text-xs md:text-sm text-[#8a7340] tracking-wide">
            Taco de madera blanca · 20×15×3 cm
          </p>
        </Reveal>
      </div>

      <Lightbox
        items={WOOD_VARIANTS}
        index={lightboxIdx}
        onClose={closeLightbox}
        onPrev={prevImg}
        onNext={nextImg}
      />
    </section>
  );
}
